export const AcceptableUsePolicy = () => {
  return (
    <div className="max-w-4xl mx-auto p-6 bg-background text-foreground">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-4">Acceptable Use Policy</h1>
        <p className="text-sm text-muted-foreground mb-2">
          <strong>Company:</strong> Realise Property Maintenance Ltd. trading as "Sortable"
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          <strong>Address:</strong> 13 Harbury Rd. Henleaze Business Centre, Henleaze, Bristol, BS9 4PN
        </p>
        <p className="text-sm text-muted-foreground mb-4">
          <strong>Telephone:</strong> 08000438880
        </p>
        <p className="text-sm text-muted-foreground">
          <strong>Last Updated:</strong> {new Date().toLocaleDateString()}
        </p>
      </div>

      <div className="space-y-6">
        <section>
          <h2 className="text-xl font-semibold mb-3">1. Purpose</h2>
          <p className="mb-3">
            This Acceptable Use Policy outlines the standards of behavior expected from all 
            users of the Sortable platform. Violation of this policy may result in account 
            suspension or termination.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">2. Prohibited Content</h2>
          <p className="mb-3">You may not post, upload, or transmit content that is:</p>
          <div className="space-y-2">
            <p>2.1. Illegal, harmful, or promotes illegal activities</p>
            <p>2.2. Threatening, abusive, harassing, or intimidating</p>
            <p>2.3. Defamatory, libelous, or invades privacy</p>
            <p>2.4. Discriminatory based on race, gender, religion, nationality, disability, sexual orientation, or age</p>
            <p>2.5. Sexually explicit or inappropriate</p>
            <p>2.6. Violent or promotes violence</p>
            <p>2.7. Spam, junk mail, or unsolicited commercial content</p>
            <p>2.8. False, misleading, or fraudulent</p>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">3. Prohibited Activities</h2>
          <div className="space-y-2">
            <p>3.1. <strong>System Abuse:</strong> Attempting to hack, disrupt, or gain unauthorized access</p>
            <p>3.2. <strong>Impersonation:</strong> Pretending to be another person or entity</p>
            <p>3.3. <strong>Fraud:</strong> Providing false information or engaging in deceptive practices</p>
            <p>3.4. <strong>Circumvention:</strong> Avoiding platform fees or taking business off-platform</p>
            <p>3.5. <strong>Multiple Accounts:</strong> Creating multiple accounts to avoid restrictions</p>
            <p>3.6. <strong>Automated Access:</strong> Using bots or scripts without permission</p>
            <p>3.7. <strong>Intellectual Property Violation:</strong> Using copyrighted material without permission</p>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">4. Communication Standards</h2>
          <div className="space-y-2">
            <p>4.1. Maintain professional and respectful communication</p>
            <p>4.2. Do not share personal contact information in initial messages</p>
            <p>4.3. Use the platform's messaging system for all job-related communication</p>
            <p>4.4. Report inappropriate messages or behavior immediately</p>
            <p>4.5. Do not send unsolicited marketing or promotional messages</p>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">5. Professional Standards</h2>
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-medium mb-2">5.1. For Tradespeople</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>Maintain appropriate professional qualifications</li>
                <li>Provide accurate information about skills and experience</li>
                <li>Complete work to industry standards</li>
                <li>Honor quoted prices and timelines</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-medium mb-2">5.2. For Customers</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>Provide accurate job descriptions</li>
                <li>Allow reasonable access for work completion</li>
                <li>Pay agreed amounts promptly</li>
                <li>Treat tradespeople with respect</li>
              </ul>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">6. Safety Requirements</h2>
          <div className="space-y-2">
            <p>6.1. Report any safety concerns immediately</p>
            <p>6.2. Do not request or perform work that violates safety regulations</p>
            <p>6.3. Ensure appropriate insurance coverage for all work</p>
            <p>6.4. Follow health and safety guidelines at all times</p>
            <p>6.5. Do not work under the influence of alcohol or drugs</p>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">7. Reporting Violations</h2>
          <div className="space-y-2">
            <p>7.1. Report violations immediately through our reporting system</p>
            <p>7.2. Provide specific details and evidence where possible</p>
            <p>7.3. Do not take matters into your own hands</p>
            <p>7.4. Cooperate with investigations</p>
            <p>7.5. False reports may result in account penalties</p>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">8. Enforcement Actions</h2>
          <p className="mb-3">Violations may result in the following actions:</p>
          <div className="space-y-2">
            <p>8.1. <strong>Warning:</strong> First-time minor violations</p>
            <p>8.2. <strong>Content Removal:</strong> Deletion of inappropriate content</p>
            <p>8.3. <strong>Account Restriction:</strong> Limited access to certain features</p>
            <p>8.4. <strong>Temporary Suspension:</strong> Account disabled for a specific period</p>
            <p>8.5. <strong>Permanent Ban:</strong> Complete removal from the platform</p>
            <p>8.6. <strong>Legal Action:</strong> Referral to law enforcement if appropriate</p>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">9. Appeals Process</h2>
          <div className="space-y-2">
            <p>9.1. You may appeal enforcement actions within 14 days</p>
            <p>9.2. Provide detailed explanation and supporting evidence</p>
            <p>9.3. Appeals are reviewed by our trust and safety team</p>
            <p>9.4. Decisions are typically made within 5 business days</p>
            <p>9.5. Repeated violations may not be eligible for appeals</p>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">10. Platform Monitoring</h2>
          <p className="mb-3">
            We reserve the right to monitor platform activity to ensure compliance 
            with this policy. This may include reviewing messages, job posts, and 
            user behavior patterns.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">11. Updates to This Policy</h2>
          <p className="mb-3">
            We may update this policy as needed to reflect changes in our platform 
            or legal requirements. Users will be notified of significant changes.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">12. Contact Information</h2>
          <div className="space-y-2">
            <p>To report violations or ask questions about this policy:</p>
            <p><strong>Email:</strong> support@sortable.co.uk</p>
            <p><strong>Phone:</strong> 08000438880</p>
            <p><strong>Address:</strong> 13 Harbury Rd. Henleaze Business Centre, Henleaze, Bristol, BS9 4PN</p>
          </div>
        </section>
      </div>
    </div>
  );
};